/**
 * The ONE eHR Viewer window (HK eHRSS compliance IN020 / IN025 / IN030 / UM040).
 *
 * Every launch targets the same window name, so the browser reuses and replaces the window:
 * opening participant B clears participant A (IN020/IN030). The handle is held here — a plain
 * module singleton with no imports — so `clearToken()` in utils/common.ts can close it on BOTH
 * logout paths (avatar menu and the 401 auto-logout) without an import cycle through the stores.
 *
 * The window is cross-origin (apps.uat.ehr.gov.hk); `closed`, `close()` and `focus()` are the
 * WindowProxy members the browser still allows us on it.
 */
export const EHR_VIEWER_WINDOW_NAME = "ehrViewer";

let viewerWindow: Window | null = null;

/**
 * Where the viewer window goes: docked to the RIGHT half of the current screen, full height,
 * so the product (left) and eHealth (right) sit side by side. eHealth cannot be embedded in
 * our page — its session cookies are SameSite-Lax and its pages declare frame-ancestors
 * 'self' (tested 2026-09-10: a framed ticket lands on eHealth's "page cannot be found") — so a
 * separate window is the only way to show it next to the EMR. The browser applies these
 * features on the FIRST open only; a reused named window keeps wherever the user put it.
 */
export function dockedWindowFeatures(scr: Screen = window.screen): string {
	const availWidth = scr.availWidth || 1280;
	const availHeight = scr.availHeight || 800;
	const width = Math.max(720, Math.floor(availWidth / 2));
	const height = availHeight;
	// availLeft/availTop are non-standard but implemented by every major browser; they place the
	// window on the screen the product is on, not the primary one.
	const availLeft = (scr as Screen & { availLeft?: number }).availLeft ?? 0;
	const availTop = (scr as Screen & { availTop?: number }).availTop ?? 0;
	const left = availLeft + availWidth - width;
	return `popup=yes,left=${left},top=${availTop},width=${width},height=${height},resizable=yes,scrollbars=yes`;
}

/** Open (or replace) the viewer window. Returns null when the browser blocked the pop-up. */
export function openEhrViewer(launchUrl: string): Window | null {
	if (!launchUrl) return null;
	const w = window.open(launchUrl, EHR_VIEWER_WINDOW_NAME, dockedWindowFeatures());
	if (w) {
		viewerWindow = w;
		try {
			w.focus();
		} catch (_e) {
			// a cross-origin window may refuse focus; the open itself already succeeded
		}
	}
	return w;
}

export function isEhrViewerOpen(): boolean {
	return !!viewerWindow && !viewerWindow.closed;
}

/** Close the viewer if we hold an open handle (IN025). Returns whether a window was closed. */
export function closeEhrViewer(): boolean {
	const w = viewerWindow;
	viewerWindow = null;
	if (!w) return false;
	try {
		if (!w.closed) {
			w.close();
			return true;
		}
	} catch (_e) {
		// nothing to do: the window is gone or the browser refused — either way we no longer track it
	}
	return false;
}
